// =============================================
// EngineProgress — Content Engine pipeline progress per node
// v3.1: + lastUpdate timestamp + stuck auto-detection (>5 min mid-pipeline)
// =============================================

import { useEffect, useState } from "react";
import { Check, Circle, Loader2, AlertTriangle } from "lucide-react";
import type { EngineStage } from "@/types/thematicMaps";

interface EngineProgressProps {
  currentStage: EngineStage;
  /** updated_at z thematic_node_content — pozwala wykryc stuck (no update >5 min) */
  updatedAt?: string | null;
}

const ENGINE_STEPS: Array<{ key: EngineStage; label: string }> = [
  { key: "serp_research", label: "SERP" },
  { key: "kg_assembly", label: "KG" },
  { key: "fan_out", label: "Fan-Out" },
  { key: "outline", label: "Outline" },
  { key: "outline_ready", label: "Struktura ✓" },
  { key: "draft", label: "Draft" },
  { key: "enrichment", label: "Enrich" },
  { key: "optimization", label: "Optimize" },
  { key: "humanization", label: "Humanize" },
  { key: "ready", label: "Gotowy" },
  { key: "published", label: "Publish" },
];

const STAGE_ORDER: Record<string, number> = {};
ENGINE_STEPS.forEach((s, i) => { STAGE_ORDER[s.key] = i; });

const STUCK_THRESHOLD_MIN = 5;
// Stages that are "in progress" (mid-pipeline) — checkpoints/terminal don't count
const IN_PROGRESS_STAGES: EngineStage[] = [
  "serp_research", "kg_assembly", "fan_out", "outline",
  "draft", "enrichment", "optimization", "humanization",
];

function formatRelative(secAgo: number): string {
  if (secAgo < 60) return `${secAgo}s temu`;
  const min = Math.floor(secAgo / 60);
  if (min < 60) return `${min} min temu`;
  const h = Math.floor(min / 60);
  return `${h}h temu`;
}

export function EngineProgress({ currentStage, updatedAt }: EngineProgressProps) {
  // Re-render every 30s to refresh "X min temu" + recompute stuck flag
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  if (currentStage === "pending") return null;

  const currentIndex = STAGE_ORDER[currentStage] ?? -1;
  const isFailed = currentStage === "failed";
  const isCheckpoint = currentStage === "outline_ready" || currentStage === "ready";
  const inProgress = IN_PROGRESS_STAGES.includes(currentStage);

  // Stuck detection: in-progress stage + no update in >5 min
  const ageSec = updatedAt ? Math.floor((Date.now() - new Date(updatedAt).getTime()) / 1000) : 0;
  const isStuck = inProgress && updatedAt && ageSec > STUCK_THRESHOLD_MIN * 60;

  // Per-stage status label
  const currentLabel = ENGINE_STEPS.find((s) => s.key === currentStage)?.label || currentStage;

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-0.5">
        {ENGINE_STEPS.map((step, i) => {
          const isDone = i < currentIndex || currentStage === "published" || (isCheckpoint && i === currentIndex);
          const isCurrent = i === currentIndex && !isFailed && !isCheckpoint;

          return (
            <div key={step.key} className="flex items-center">
              <div
                className={`
                  flex items-center justify-center w-5 h-5 rounded-full text-[8px] font-medium
                  ${isDone
                    ? "bg-green-500/15 text-green-700 dark:text-green-400"
                    : isCurrent && isStuck
                      ? "bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/50"
                      : isCurrent
                        ? "border border-primary text-primary"
                        : isFailed && i === currentIndex
                          ? "bg-red-500/15 text-red-600 dark:text-red-400"
                          : "bg-muted text-muted-foreground"
                  }
                `}
                title={`${step.label}${updatedAt && i === currentIndex ? ` — ${formatRelative(ageSec)}` : ""}`}
              >
                {isDone ? (
                  <Check className="h-2.5 w-2.5" />
                ) : isCurrent && isStuck ? (
                  <AlertTriangle className="h-2.5 w-2.5" />
                ) : isCurrent ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                ) : (
                  <Circle className="h-2 w-2" />
                )}
              </div>
              {i < ENGINE_STEPS.length - 1 && (
                <div
                  className={`w-2 h-px ${
                    isDone ? "bg-green-500/60" : "bg-border"
                  }`}
                />
              )}
            </div>
          );
        })}
        {isFailed && (
          <span className="ml-1 text-[10px] text-red-600 dark:text-red-400 font-medium">BŁĄD</span>
        )}
      </div>

      {/* Status line — current stage label + last update + stuck warning */}
      {(updatedAt || isStuck) && currentStage !== "ready" && currentStage !== "published" && currentStage !== "failed" && (
        <div className={`text-[10px] flex items-center gap-1.5 ${isStuck ? "text-amber-600 dark:text-amber-400 font-medium" : "text-muted-foreground"}`}>
          {isStuck && <AlertTriangle className="h-2.5 w-2.5" />}
          <span>{currentLabel}{updatedAt ? ` · ${formatRelative(ageSec)}` : ""}{isStuck ? " · zacieta? sprobuj Reset" : ""}</span>
        </div>
      )}
    </div>
  );
}
